<!-- ダッシュボード/講座詳細 資料選択時のページ -->
<script setup lang="ts">
  import { useFilterStore, documentFilterValues, documentFilters, documentSortItems } from "~/stores/FilterStore";
  import { useDocumentStore } from "~/stores/DocumentStore";
  import { CONTENT_PAGE_SIZE } from "~/consts/contentsConfig";
  import { PlanInfo } from "~/entities/PlanInfo";

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {
    planInfo: undefined,
  });

  interface Emits {
    (event: "onBack"): void;
  }
  defineEmits<Emits>();

  const filterStore = useFilterStore();
  const documentStore = useDocumentStore();

  const pageSize = ref(CONTENT_PAGE_SIZE);

  const isSp = isSpDisplayRef();

  const page = computed({
    get: () => {
      return filterStore.documentPage;
    },
    set: value => {
      filterStore.documentPage = value;
    },
  });

  const search = computed({
    get: () => {
      return filterStore.searchWordDocument;
    },
    set: value => {
      filterStore.searchWordDocument = value;
    },
  });

  onMounted(() => {
    filterStore.resetWithPlanId(localProps.planInfo.id);
    fetch();
  });

  // ページネーションを監視
  watch(
    () => {
      return page.value;
    },
    (newValue, oldValue) => {
      if (oldValue !== undefined) {
        fetch();
      }
    },
    { immediate: true },
  );

  watch(
    () => {
      return search.value;
    },
    () => {
      fetch();
    },
  );

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = async () => {
    try {
      await filterStore.fetchDocumentWithFilter(page.value, pageSize.value, localProps.planInfo.id);
      if (page.value > 1 && !documentStore.documents?.contents?.length) {
        // 2ページ目以降指定でデータがない場合は1ページ目に戻す
        page.value = 1;
      }
    } catch (error) {
      // エラー処理
      consoleLog(error);
    }
  };

  const handleChangeFilter = (value: string) => {
    filterStore.documentPage = 1;
    filterStore.setSelectedDocumentFilter(value);
    fetch();
  };

  const changeSort = (value: string) => {
    if (filterStore.selectedDocumentSortItem === value) {
      filterStore.selectedDocumentSortOrder = filterStore.selectedDocumentSortOrder === "desc" ? "asc" : "desc";
    } else {
      filterStore.selectedDocumentSortItem = value;
      filterStore.selectedDocumentSortOrder = "desc";
    }
    fetch();
  };

  const sortItems = computed(() => {
    return documentSortItems;
  });

  const loading = computed<boolean>(() => documentStore.loading);

  defineExpose({
    fetch,
    loading,
  });
</script>

<template>
  <MoleculesContainerMain
    v-model:search="search"
    :show-search="true"
    title="資料一覧"
    svg-icon="assets/images/icon/menu/document.svg"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <!-- フィルター -->
      <v-menu scroll-strategy="close">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            icon="$mdiFilter"
            :size="isSp ? 'small' : 'default'"
            :color="filterStore.getSelectedDocumentFilter().value !== documentFilterValues.All ? 'primary' : 'default'"
          />
        </template>
        <v-sheet class="bg-monochrome-04">
          <div class="py-2">
            <div
              v-for="item in documentFilters"
              :key="item.value"
              class="d-flex justify-space-between cursor-pointer py-2 px-3"
              @click="handleChangeFilter(item.value)"
            >
              <div class="pr-8">{{ item.title }}</div>
              <div>{{ item.value === filterStore.selectedDocumentFilter ? "✔" : "" }}</div>
            </div>
          </div>
        </v-sheet>
      </v-menu>
      <!-- ソート -->
      <MoleculesHeaderSortMenu
        :sort-order="filterStore.selectedDocumentSortOrder"
        :selected-value="filterStore.selectedDocumentSortItem"
        :sort-items="sortItems"
        @change-sort="changeSort"
      />
    </template>
    <OrganismsDocumentCardGrid
      v-model:page="page"
      :show-header="false"
      :total-pages="documentStore.documents?.totalPages"
      :items="documentStore.documents?.contents"
      :loading="loading"
      empty-message="資料が提供されていません。"
      @on-back="$emit('onBack')"
    />
  </MoleculesContainerMain>
</template>
